/**
 * LES-LEX — Role display helpers.
 *
 * Backend zwraca `role` (techniczny string: "owner", "admin", "lawyer", ...) +
 * dodatkowy flag `is_platform_owner: bool` na obiekcie User. Tu robimy zamiane
 * na czytelne dla uzytkownika labele rozrozniajace platform ownera (Miro)
 * od org ownera (np. Szymon — wlasciciel kancelarii).
 *
 * Uzycie:
 *   import { roleLabel } from "@/lib/roleLabel";
 *   <Badge>{roleLabel(user, t)}</Badge>
 */

const FALLBACK = {
  pl: {
    platform_owner: "Wlasciciel platformy",
    org_owner: "Wlasciciel kancelarii",
    owner: "Wlasciciel",
    admin: "Admin",
    lawyer: "Prawnik",
    paralegal: "Asystent",
    viewer: "Podglad",
  },
  en: {
    platform_owner: "Platform Owner",
    org_owner: "Law Firm Owner",
    owner: "Owner",
    admin: "Admin",
    lawyer: "Lawyer",
    paralegal: "Paralegal",
    viewer: "Viewer",
  },
};

/**
 * Convert role + platform-owner flag into a localised display label.
 *
 * @param {object|null} user - user object with at least `role` and optional
 *                             `is_platform_owner` boolean.
 * @param {function} [t]     - i18next translator. If provided, tries
 *                             `t("roles.platform_owner")` etc. first.
 * @param {string} [lang]    - fallback locale ("pl" by default).
 */
export function roleLabel(user, t, lang = "pl") {
  if (!user || !user.role) return "";

  let key;
  if (user.role === "owner") {
    key = user.is_platform_owner ? "platform_owner" : "org_owner";
  } else {
    key = user.role;
  }

  if (typeof t === "function") {
    const translated = t(`roles.${key}`, { defaultValue: "" });
    if (translated) return translated;
  }
  const dict = FALLBACK[lang] || FALLBACK.pl;
  return dict[key] || dict[user.role] || user.role;
}

/**
 * Variant that takes an org member object (from `org.members[]` list — has
 * `role` field but no `is_platform_owner` because members of an org are by
 * definition NOT platform owners; "owner" here always means org owner).
 *
 * @param {object} member  - { user_id, role }
 * @param {function} [t]
 * @param {string} [lang]
 */
export function memberRoleLabel(member, t, lang = "pl") {
  if (!member || !member.role) return "";
  // W kontekscie organizacji "owner" zawsze == wlasciciel kancelarii
  const fakeUser = { role: member.role, is_platform_owner: false };
  return roleLabel(fakeUser, t, lang);
}
